import { ThumbsDown, ThumbsUp } from "lucide-react";
import { StyleSheet, View, Text, Pressable } from "react-native";
import { useState } from "react";
import { Copy, Check } from "@phosphor-icons/react";

import { AttachmentChip, AttachmentChipProps } from "../attachmentChip/AttachmentChip";
import { TaskChip, TaskChipProps } from "../taskChip/TaskChip";
import { colors, radii, spacing, typographyStyles } from "../../../../styles/design-system";
import CustomMarkdown from "../../../common/general/CustomMarkdown/CustomMarkdown";
import useCopyContent from "../../../../hooks/useCopyContent";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import Tooltip from "../../../common/tooltip/Tooltip";

export interface MessageBubbleProps {
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  taskAttachmentChips?: AttachmentChipProps[];
  taskChips?: TaskChipProps[];
  avatar?: {
    backgroundUrl?: string;
    initials?: string;
  };
  onFeedback?: (value: "up" | "down") => void;
  feedback?: "up" | "down";
  isArchived: boolean;
}

export const MessageBubble = ({
  content,
  role,
  timestamp,
  taskAttachmentChips = [],
  taskChips = [],
  onFeedback,
  feedback,
  isArchived,
}: MessageBubbleProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showCheckMark, setShowCheckMark] = useCopyContent() as [boolean, (value: boolean) => void];

  const handleCopyPress = () => {
    navigator.clipboard.writeText(content);
    setShowCheckMark(true);
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const messageStyles = [styles.messageBubble, role === "user" ? styles.userMessage : styles.assistantMessage];
  const bubbleStyles = [
    styles.bubble,
    role === "user" ? styles.userBubble : styles.assistantBubble,
    role === "user" && isArchived && styles.userArchivedBubble,
  ];
  const messageWrapperStyles = [
    styles.messageWrapper,
    role === "user" ? styles.userMessageWrapper : styles.assistantMessageWrapper,
  ];
  const textStyles = [
    styles.message,
    typographyStyles.textBase,
    role === "user" ? styles.userText : styles.assistantText,
    role === "user" && isArchived && styles.userArchivedText,
  ];
  return (
    <View style={messageStyles}>
      <View style={messageWrapperStyles}>
        {(taskAttachmentChips ?? []).map((attachment, index) => (
          <AttachmentChip key={index} {...attachment} removeChip={isArchived ? undefined : attachment.removeChip} />
        ))}
        <View style={bubbleStyles}>
          <CustomMarkdown
            style={{
              body: textStyles,
              ...(role === "user" && {
                paragraph: { marginTop: 0, marginBottom: 0 },
              }),
            }}
            content={content}
          />
          {(taskChips ?? []).map((attachment, index) => (
            <View style={{ marginTop: spacing.sm }}>
              <TaskChip key={index} {...attachment} />
            </View>
          ))}
        </View>
        <View style={styles.actionBar}>
          {!isArchived && role === "assistant" && onFeedback != null && (
            <>
              <Pressable
                style={[styles.voteBtn, feedback === "up" && styles.upvoteActive]}
                onPress={() => {
                  onFeedback("up");
                }}>
                <ThumbsUp
                  size={16}
                  fill={feedback === "up" ? colors.success[600] : "none"}
                  color={feedback === "up" ? "transparent" : undefined}
                />
              </Pressable>
              <Pressable
                style={[styles.voteBtn, feedback === "down" && styles.downvoteActive]}
                onPress={() => {
                  onFeedback("down");
                }}>
                <ThumbsDown
                  size={16}
                  fill={feedback === "down" ? colors.error[600] : "none"}
                  color={feedback === "down" ? "transparent" : undefined}
                />
              </Pressable>
            </>
          )}
          {role === "assistant" && (
            <Tooltip content="Copy" offset={-10} horizontalOffset={-20}>
              <Pressable
                style={styles.copyContainer}
                // @ts-ignore - onMouseEnter/onMouseLeave work on web
                onMouseEnter={handleMouseEnter}
                // @ts-ignore - onMouseEnter/onMouseLeave work on web
                onMouseLeave={handleMouseLeave}>
                {!showCheckMark && (
                  <CustomButton
                    styleType="transparent"
                    size="s"
                    onPress={handleCopyPress}
                    aria-label="Copy message"
                    style={styles.copyButton}
                    leftIcon={<Copy size={16} color={!isHovered ? "#9b9b9b" : undefined} />}
                  />
                )}
                {showCheckMark && (
                  <View style={styles.checkIcon}>
                    <Check size={16} color={!isHovered ? "#9b9b9b" : undefined} />
                  </View>
                )}
              </Pressable>
            </Tooltip>
          )}
          {/* temporary fix for time display */}
          <Text style={styles.timestamp}>
            {new Date(timestamp).toLocaleString(undefined, {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
              second: "numeric",
              hour12: true,
            })}
          </Text>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  messageBubble: {
    alignSelf: "stretch",
    display: "flex",
    alignItems: "flex-start",
    gap: spacing.sm,
    marginTop: spacing.xs,
  },
  userMessage: {
    flexDirection: "row-reverse",
  },
  assistantMessage: {
    flexDirection: "row",
  },
  bubble: {
    borderRadius: radii.lg,
  },
  userBubble: {
    padding: spacing.sm,
    borderTopRightRadius: radii.sm,
    backgroundColor: colors.brand[500],
  },
  userArchivedBubble: {
    backgroundColor: colors.grey[400],
  },
  userArchivedText: {
    color: colors.white,
  },
  assistantBubble: {
    backgroundColor: "transparent",
  },
  messageWrapper: {
    display: "flex",
    maxWidth: "90%",
  },
  userMessageWrapper: {
    alignItems: "flex-end",
  },
  assistantMessageWrapper: {
    alignItems: "flex-start",
  },
  message: {
    textWrap: "wrap",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    // @ts-expect-error - works for web
    display: "block",
  },
  userText: {
    textAlign: "right",
    color: colors.white,
  },
  assistantText: {
    textAlign: "left",
    color: colors.grey["700"],
    // @ts-expect-error - works for web
    display: "block",
  },
  actionBar: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: spacing.xs,
    marginTop: spacing.xxs,
    gap: spacing.xs,
    color: colors.grey[500],
    ...typographyStyles.textXs,
  },
  voteBtn: {
    padding: spacing.xs,
    borderRadius: radii.full,
  },
  upvoteActive: {
    backgroundColor: colors.success[50],
  },
  downvoteActive: {
    backgroundColor: colors.error[100],
  },
  timestamp: {
    color: colors.grey[500],
    ...typographyStyles.textXs,
  },
  copyContainer: {
    width: 28,
    height: 28,
    justifyContent: "center",
    alignItems: "center",
  },
  copyButton: {
    padding: 5,
    width: 28,
    height: 28,
  },
  checkIcon: {
    padding: 5,
    height: 28,
    width: 28,
  },
});
